import axios from 'axios'
import { computed, ComputedRef, reactive } from 'vue'

import { HttpRouter } from "@Modules/shared/interfaces/HttpRouter";

import TopReferrerStats from '../DTOs/TopReferrerStats'
import { FetchForAliasArgs, FetchForTagArgs, FetchForTeamArgs } from './types'

/**
 * Represents the resonse from the server. Eventually this type will be auto generated
 * by the backend but for now its here
 */
export type TopReferrerResponseItem = {
  source: string
  hits: number
}
export type TopReferrerResponseData = {
  dataset: TopReferrerResponseItem[]
  direct: number
  total: number
}

export type iTopReferrerStatsAPI = {
  dataset: ComputedRef<Readonly<TopReferrerStats>>
  loading: ComputedRef<boolean>
  setRouter: (router: HttpRouter<'get'>) => iTopReferrerStatsAPI
  fetchForTeam: (args: FetchForTeamArgs) => Promise<void>
  fetchForAlias: (args: FetchForAliasArgs) => Promise<void>
  fetchForTag: (args: FetchForTagArgs) => Promise<void>
}

/**
 * Returns data that is a valid Popularity Stat
 */
export function DefaultTopReferrerStats(): TopReferrerResponseItem[] {
  let retval: TopReferrerResponseItem[] = []
  const sources = [
    'www.cats.com',
    'www.dogs.com',
    'www.horse.com',
    'www.pig.com',
  ]
  let i = 1
  Object.values(sources).forEach((source: string) => {
    retval.push({ source, hits: i * 11 })
    i++
  })

  return retval
}

/**
 * A tool to allow you to fetch and interact with "popular times" analytics
 * data fetched from the server.
 */
export default function TopReferrerStatsAPI(
  debug = false
): iTopReferrerStatsAPI {
  const state = reactive<{
    dataset: TopReferrerStats
    loading: boolean
  }>({
    dataset: new TopReferrerStats(),
    loading: false,
  })

  let router: HttpRouter<'get'> = axios

  async function _fetch(url: string, from: string, to: string): Promise<void> {
    state.loading = true

    if (debug) {
      state.dataset = new TopReferrerStats(
        DefaultTopReferrerStats(),
        11,
        121,
        from,
        to
      )
    } else {
      const response: HttpResponse<TopReferrerResponseData> =
        await router.get(url)

      if (response.status !== 200) {
        throw new Error('Could not fetch top referrer stats data from ' + url)
      }

      const theData = response.data
      state.dataset = new TopReferrerStats(
        theData.dataset,
        theData.direct,
        theData.total
      )
    }

    state.loading = false
  }

  return {
    loading: computed(() => state.loading),
    dataset: computed(() => state.dataset),

    fetchForTeam: async function ({
      orgID,
      teamID = null,
      from,
      to,
      archived = false,
    }): Promise<void> {
      const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/sources/top?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`
      return _fetch(url, from, to)
    },

    fetchForAlias: async function ({
      orgID,
      teamID = null,
      alias,
      from,
      to,
      archived = false,
    }): Promise<void> {
      const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/sources/top?alias[]=${encodeURIComponent(alias)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`
      return _fetch(url, from, to)
    },

    fetchForTag: async function ({
      orgID,
      teamID = null,
      tag,
      from,
      to,
      archived = false,
    }): Promise<void> {
      const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/sources/top?tag[]=${encodeURIComponent(tag)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`
      return _fetch(url, from, to)
    },

    /**
     * Allows you to swap out the router for a fake one when writing tests
     */
    setRouter(newRouter) {
      router = newRouter
      return this
    },
  }
}
